import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'motion/react';

const stats = [
  { value: 15, suffix: '+', label: 'Years in Business', icon: 'ri-calendar-check-line' },
  { value: 10000, suffix: '+', label: 'Happy Customers', icon: 'ri-user-smile-line' },
  { value: 12, suffix: '+', label: 'Trusted Brands', icon: 'ri-award-line' },
  { value: 99, suffix: '%', label: 'Satisfaction Rate', icon: 'ri-heart-3-line' },
];

function AnimatedCounter({ value, suffix }: { value: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 2000;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, value]);

  const display = value >= 1000 ? `${(count / 1000).toFixed(count >= 1000 ? 0 : 1)}K` : count;

  return (
    <span ref={ref} className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-400 tabular-nums">
      {display}{suffix}
    </span>
  );
}

export default function StatsSection() {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_d48a661c85c1dc5e775ca9366cc2c517_cadbae82.jpg"
          alt="Ashok Communication at Night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-foreground-950/85"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block bg-primary-500/20 text-primary-300 px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold mb-4">
            Trusted Since 2010
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-3">
            Why Choose Us
          </h2>
          <p className="text-background-400 text-sm md:text-base max-w-xl mx-auto">
            Numbers that speak for our commitment to quality, trust, and customer satisfaction
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: index * 0.12, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.02 }}
              className="bg-background-50/10 backdrop-blur-sm border border-background-50/10 rounded-xl p-6 md:p-8 text-center hover:border-primary-500/40 hover:bg-background-50/15 transition-all duration-300"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-4 flex items-center justify-center bg-primary-500/20 rounded-xl">
                <i className={`${stat.icon} text-xl md:text-2xl text-primary-400`}></i>
              </div>
              <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              <p className="text-background-400 text-xs md:text-sm font-medium mt-2">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}